import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    IconButton,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../Redux/Store/Hooks";
import { getRoleMapping } from "../../Redux/Slices/RoleMapping/RoleMappingList";
import RoleScreenMappingModal from "./RoleScreenMappingModal";
import { IRoleMapping } from "./IRoleMappingMaster";

const RoleMappingMaster: React.FC = () => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(undefined);

    const { data: roleMappingData, isLoading } = useAppSelector(
        (state) => state.RoleMappingScreens
    );

    useEffect(() => {
        dispatch(getRoleMapping());
    }, [dispatch]);

    const roles: IRoleMapping[] = roleMappingData?.data || [];
    const loading = isLoading || false;
    const error = roleMappingData?.status === false ? roleMappingData?.message : null;

    const handleEdit = (roleId: number) => {
        setSelectedRoleId(roleId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedRoleId(undefined);
        // Refresh the role mapping data after closing modal
        dispatch(getRoleMapping());
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            {/* <Typography variant="h5" fontWeight={600} mb={3}>
                Role Mapping Master
            </Typography> */}

            <TableContainer
                component={Paper}
                sx={{
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
                    borderRadius: 2,
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: "#b2ebf2",
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "#00838f",
                                }}
                            >
                                S.No
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "#00838f",
                                }}
                            >
                                Role
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "#00838f",
                                }}
                            >
                                Total Screen
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "#00838f",
                                }}
                                align="center"
                            >
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No roles found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            roles.map((role, index) => (
                                <TableRow
                                    key={role.role_id}
                                    sx={{
                                        "&:hover": { backgroundColor: "#f9f9f9" },
                                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                                    }}
                                >
                                    <TableCell>
                                        <Typography fontWeight={500}>{index + 1}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={500}>{role.role_name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography>{role.screen_count}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            onClick={() => handleEdit(role.role_id)}
                                            sx={{
                                                color: "#00bcd4",
                                                "&:hover": {
                                                    backgroundColor: "#e0f7fa",
                                                },
                                            }}
                                            size="small"
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                    Total Roles: {roles.length}
                </Typography>
            </Box>

            {/* Role Screen Mapping Modal */}
            <RoleScreenMappingModal
                open={modalOpen}
                onClose={handleCloseModal}
                roles={roles}
                selectedRoleId={selectedRoleId}
            />
        </Box>
    );
};

export default RoleMappingMaster;
